import { type FC, useCallback, useEffect, useState } from 'react';
import { mergeRefs } from '@/helpers/common';
import { ThemeMode } from '@/helpers/enums';
import { useHorizontalScroll } from '@/hooks/common';
import { useDevice } from '@/hooks/device';
import { useElementSize } from '@/hooks/elementSize';
import useEventListener from '@/hooks/eventListener';
import { useNavigation } from '@/hooks/navigation';
import { useDetectShadow } from '@/hooks/shadow';
import { paddingMob } from '@/styles/helpers';
import { IconComponent } from '../IconComponent';
import { isCategory, type NavItem } from './helpers';
import { NavigationItem } from './NavigationItem';
import { ScCategoriesWrapper, ScTabNavigation } from './styled';

const SCROLL_GAP = 500;

interface TabNavigationProps {
  navItems: NavItem[];
  selectedItem: number;
  setSelectedItem?: (index: number) => void;
  menuItemWidth?: string;
  maxWidth100?: boolean;
  fullScreen?: boolean;
  containerWidth?: number;
  withoutShadow?: boolean;
  withBorderMarker?: boolean;
  withUnderlineMarker?: boolean;
  mode?: ThemeMode;
  withArrows?: boolean;
  handleNavigationItemClick?: (id: string) => void;
}

export const TabNavigation: FC<TabNavigationProps> = ({
  navItems,
  selectedItem,
  setSelectedItem,
  menuItemWidth,
  mode = ThemeMode.Dark,
  withArrows,
  handleNavigationItemClick,
  ...props
}) => {
  const { isMobileDevice } = useDevice();

  const { generateLink } = useNavigation();
  const refWithScroll = useHorizontalScroll();
  const [ref, { width }] = useElementSize();
  const { ref: shadowRef, itemsRef, isLeft, isRight } = useDetectShadow();

  const [containerClientWidth, setContainerClientWidth] = useState<number>(0);

  const [selectedItemPosition, setSelectedItemPosition] = useState({
    left: 0,
    width: 0,
  });

  const [padding, setPadding] = useState(0);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      ref.current?.scroll({
        left: selectedItemPosition.left,
        behavior: 'smooth',
      });
    }, 200);

    return () => clearTimeout(timeoutId);
  }, [ref, selectedItemPosition.left]);

  const handleSelectItem = useCallback(
    (index: number, id: string | null) => {
      if (id) handleNavigationItemClick && handleNavigationItemClick(id);
      setSelectedItem && setSelectedItem(index);
    },
    [setSelectedItem, handleNavigationItemClick]
  );

  const paddingLeftRight = useCallback(() => {
    const windowWidth = window.innerWidth;

    if (isMobileDevice) {
      setPadding((windowWidth - containerClientWidth + paddingMob * 2) / 2);
    }
  }, [isMobileDevice, containerClientWidth]);

  useEventListener('resize', paddingLeftRight, undefined, true);

  const [paddingLeft, setPaddingLeft] = useState(0);

  const handleScrollLeft = () => {
    setPaddingLeft((state) => state - SCROLL_GAP);
  };

  const handleScrollRight = () => {
    setPaddingLeft((state) => state + SCROLL_GAP);
  };

  useEffect(() => {
    ref.current?.scroll({
      left: paddingLeft,
      behavior: 'smooth',
    });
  }, [paddingLeft, ref]);

  return (
    <ScCategoriesWrapper
      padding={padding}
      paddingMob={paddingMob}
      ref={(node) => {
        if (node) {
          setContainerClientWidth(node.clientWidth);
        }
      }}
      {...props}
    >
      <div className="mob-full">
        <ScTabNavigation
          isShadowLeft={isLeft}
          isShadowRight={isRight}
          left={selectedItemPosition.left}
          mode={mode}
          padding={padding}
          ref={mergeRefs(ref, refWithScroll, shadowRef)}
          width={selectedItemPosition.width}
          {...props}
        >
          {withArrows && isLeft && (
            <button
              aria-label="Scroll left"
              className="btn-left"
              onClick={handleScrollLeft}
            >
              <IconComponent className="arrow-icon" name="whiteArrowRight" />
            </button>
          )}
          {withArrows && isRight && (
            <button
              aria-label="Scroll right"
              className="btn-right"
              onClick={handleScrollRight}
            >
              <IconComponent className="arrow-icon" name="whiteArrowRight" />
            </button>
          )}
          <div ref={itemsRef}>
            {selectedItem >= 0 && <div className="marker" />}
            {navItems.map((navItem, index) => {
              const isItemCategory = isCategory(navItem);
              const itemId = isItemCategory ? navItem.id : null;
              const title = isItemCategory ? navItem.title : navItem;
              const link = isItemCategory
                ? generateLink(navItem.pathname)
                : undefined;

              return (
                <NavigationItem
                  handleSelectItem={handleSelectItem}
                  idx={index}
                  isSelected={index === selectedItem}
                  itemId={itemId}
                  key={index}
                  link={link}
                  mode={mode}
                  navItemsCount={navItems.length}
                  setSelectedItemPosition={setSelectedItemPosition}
                  title={title}
                  width={menuItemWidth}
                  widthOfMenu={width}
                  {...props}
                />
              );
            })}
          </div>
        </ScTabNavigation>
      </div>
    </ScCategoriesWrapper>
  );
};
